import { Action } from "components/actions/actions";
import { Response } from "components/actions/response";
import {
    ComponentWithOptions,
    InputComponent,
} from "components/components/Component";
import { LabelElement } from "components/components/label/labelElement";
import {
    SearcherBaseDefinition,
    SearcherCodeDefinition,
    SearcherDetailDefinition,
    SearcherBaseComponent
} from "components/input/searcherWithCode/baseSearcherDefinition";
import i18n from "components/i18n/i18n";
import { ComponentActionHelper } from "components/events/componentActionHelper";
import { fireDataChangedEvent, fireInitDataChangedEvent } from "components/form/dataChangedEventDetail";
import { FocusManager } from "components/pages/focusManager";
import { OptionHolder, SearcherParameter } from "components/input/select/options";
import * as message from "components/message/message";
import { TableDefinitionUtils } from "components/table/tableDefinitionUtils";
import IMask from "imask";
import $ from "jquery";
import { SearcherWithComposedCode } from "components/input/searcherWithCode/searcherWithComposedCode/searcherWithComposedCode";
import * as itemFinderModal from "components/input/searcherWithCode/baseFunctionsFinderModal";
import { mergeOptions } from "components/input/searcherWithCode/OptionsMergeUtil";
import template from "./searcherWithSingleCode.hbs";
import "./searcherWithSingleCode.scss";

export class SearcherWithSingleCode extends SearcherBaseComponent<SearcherWithSingleCodeDefinition> implements InputComponent, ComponentWithOptions {

    public readonly componentType: string = "searcherWithSingleCode";

    private oldValue: string | number;
    private currentValue;
    /** 
     * The user has changed the code.
     * We fire a datachange event with the new code.
     * We save the code to wait for detail update. 
     * Detail update should fire an initialisation event if the code has not changed. 
     */
    private newCode: string | number
    private valid = true;
    private validationMessage: string = null;
    private visited = false;
    private queryParameters: SearcherParameter[];
    private codeInput: HTMLInputElement;
    private detailInput: HTMLInputElement;
    private magnifierButton: HTMLButtonElement;

    private mask: IMask.InputMask<IMask.AnyMaskedOptions>;

    constructor(schema: SearcherWithSingleCodeDefinition) {
        super(schema);
        if (schema.finder != null && schema.outputParameterMappings != null) {
            schema.finder.outputParameterMappings = schema.outputParameterMappings;
        }
    }

    public render(): Element {
        this.renderTemplate();
        this.bindEvents();
        ComponentActionHelper.register(this);
        super.tooltipBindElement(".o-form-component__input-group");
        this.initMask();
        return this.view;
    }

    public renderTemplate(): Element {
        let objParams: any = this.generateParamsTemplate();
        super.renderTemplate(template, objParams);
        return this.view;
    }

    private generateParamsTemplate(): any {
        let objParams: any = {};
        // Don't hint the code input size if the component has detail or if it is used in a form (labelKey)
        const mustFitCode = !(!this.definition.hasDetail && this.definition.labelKey != null) && this.definition.maxlength != null;
        objParams.mustFitCode = mustFitCode;

        return objParams;
    }

    private bindEvents(): void {

        this.detailInput = this.view.querySelector(".c-searcher-with-single-code__detail") as HTMLInputElement;
        this.codeInput = this.view.querySelector(".c-searcher-with-single-code__code") as HTMLInputElement;
        this.magnifierButton = this.view.querySelector(".c-searcher-with-single-code__magnifier");

        this.codeInput.addEventListener("focus", () => {
            this.visited = true;
        });
        this.codeInput.addEventListener("focusout", () => {
            this.setValidationStatus(this.valid, this.validationMessage);
        });
        this.codeInput.addEventListener("change", this.changeListener);

        if (this.definition.tabOnEnter === true) {
            this.codeInput.addEventListener("keydown", (e) => {
                if (e.key === "Enter") {
                    e.stopPropagation();
                    e.preventDefault();
                    FocusManager.focusNext();
                }
            });
        }

        if (this.definition.tabOnEnterButton === true) {
            this.magnifierButton.addEventListener("keydown", (e) => {
                if (e.key === "Enter") {
                    e.stopPropagation();
                    e.preventDefault();
                    FocusManager.focusNext();
                }
            });
        }

        if (this.definition.tabOnEnterDetail === true && this.definition.hasDetail) {
            this.detailInput.addEventListener("keydown", (e) => {
                if (e.key === "Enter") {
                    e.stopPropagation();
                    e.preventDefault();
                    FocusManager.focusNext();
                }
            });
        }

        this.magnifierButton.addEventListener("click", this.clickMagnifiefCallback);
    }

    private changeListener = (evt) => {
        this.newCode = this.codeInput.value;
        if (this.newCode == null || this.newCode.trim() === "") {
            this.newCode = null;
        }
        if (this.definition.numericCode) {
            this.newCode = parseInt(this.codeInput.value, 10);
            if (isNaN(this.newCode)) {
                this.newCode = null;
            }
        }
        let v = null;
        if (this.newCode != null) {
            v = { code: this.newCode };
        }
        if (!(this.definition.outputParameterMappings != null && this.definition.outputParameterMappings.length > 0 && this.newCode == null)) {
            // Don't fire the event because and event will be fired from the container (form or table row) 
            // and it is enough to fired one event
            fireDataChangedEvent(this, this.view, v, this.currentValue);
            this.value(v)
        }
        this.detailUpdate();
        this.newCode = undefined;
    }

    private clickMagnifiefCallback = () => {
        const params = {
            data: {
                filter: this.queryParameters,
            },
        };
        itemFinderModal.open(
            itemFinderModal.EnumSearcherWithType.SINGLE,
            { ...this.definition.finder, outputParameterMappings: this.definition.outputParameterMappings },
            params,
            this.modalWindowRowSelectedCallback);
    }

    private initMask(): void {
        if (this.mask == null) {
            if (this.definition.numericCode && typeof this.definition.numericCode !== 'undefined' && !this.definition.allowedPattern) {
                this.initMaskNumberOptions();
            }
            if (!this.definition.numericCode && this.definition.allowedPattern && typeof this.definition.allowedPattern !== 'undefined') {
                this.initMaskRegExp();
            }
        }
    }

    private initMaskNumberOptions(): void {
        const signed = true;
        const max = this.definition.maxlength ?
            (Math.pow(10, this.definition.maxlength) - 1) :
            Number.MAX_SAFE_INTEGER;
        const maskOptions = {
            scale: 0,
            mask: Number,
            min: 0,
            max,
            signed,
            thousandsSeparator: "",
        } as any;
        this.mask = IMask(this.codeInput, maskOptions);
    }

    private initMaskRegExp(): void {
        const maskOptions = {
            mask: new RegExp(this.definition.allowedPattern),
        } as any;
        this.mask = IMask(this.codeInput, maskOptions);
    }

    /**
     * Callback to run when a row has been selected in the modal window.
     * @param data The data of the selected row
     */
    private modalWindowRowSelectedCallback = (data: any): void => {
        const oldValue = this.getValue();
        if (data.detail === undefined) {
            if (this.definition.hasDetail) {
                this.value({ code: data.code, detail: "" });
            } else {
                this.value({ code: data.code });
            }
        } else {
            this.value({ code: data.code, detail: data.detail });
        }
        // string, number or null
        // tslint:disable-next-line: triple-equals
        if (oldValue?.code != data?.code || oldValue?.detail != data?.detail) {
            if (Object.keys(data).length > 2) {
                // We have more than code and detail.
                // Thus we must fire a data change request to the container compnnent (form or table row)
                delete data.code;
                delete data.detail;
                data[this.definition.property] = this.getValue();
                //If there is an event associated to the component, it must be fired from it
                if (this.definition.eventActions !== undefined && this.definition.eventActions.length !== undefined && this.definition.eventActions.length > 0) {
                    fireDataChangedEvent(this, this.view, this.getValue(), oldValue);
                }
                this.fireDataChangeRequest(data);
                // If we change data in other fields of the form we don't need 
                // to fire data change event as it will be fired by the form
            } else {
                if (oldValue?.code != data?.code) {
                    // the code value is changing
                    if (this.newCode === undefined) {
                        // The change doesn't come from a code input change
                        fireDataChangedEvent(this, this.view, this.getValue(), oldValue);
                    } else if (this.newCode === this.currentValue?.code || this.currentValue == null && this.newCode === null) {
                        // We are managing here the response from the server to fill de detail and to check tahat the data makes sense
                        if (this.definition.hasDetail !== false && !(this.newCode == null && this.currentValue?.detail == null)) {
                            // The change comes from a change in the code input and we are now filling the detail
                            // We notify the container that a change has happened bau that it really doest not change imporatnt data, 
                            // that it just initialises de value of the detail
                            fireInitDataChangedEvent(this, this.view, this.getValue(), oldValue);
                        }
                    }
                } else {
                    // the component has been initialised with data without detail and now it is updating the detail
                    fireInitDataChangedEvent(this, this.view, this.getValue(), oldValue);
                }
            }
        }
    }

    private fireDataChangeRequest(data: any) {
        if (this.definition.outputParameterMappings != null && this.definition.outputParameterMappings.length > 0) {
            const eventData = {
                data,
                component: this
            };
            const event = new CustomEvent("dataChangeRequest", {
                bubbles: true,
                detail: eventData
            });
            this.view.dispatchEvent(event);
        }
    }

    private detailUpdate(silent = false) {
        let codeValue = this.codeInput.value;
        if (codeValue == null || codeValue.trim() === "") {
            this.updateData(null);
            return;
        } else {
            if (this.definition.uppercase) {
                codeValue = this.toUpperCaseCode(codeValue)
            }
        }

        if (this.definition.finder == null) {
            console.warn("No finder defined for searcher " + this.definition.property);
            return;
        }
        // The action of the filter
        let actionDefintion;
        if (this.definition.finder.table != null) {
            actionDefintion = this.definition.finder.table.filter.actions[0].action;
        } else {
            actionDefintion = {
                url: this.definition.finder.url + "/record.json",
            }
        }
        const action = new Action(actionDefintion, this);
        action.done = this.detailUpdateDoneCallback(silent);
        action.definition.actionName = "action-" + this.definition.id;
        const queryOptions = {};
        for (const queryParameter of this.queryParameters ?? []) {
            queryOptions[queryParameter.property] = queryParameter.value;
        }
        action.run({ code: codeValue, ...queryOptions });
    }


    private toUpperCaseCode = (codeValue) => {
        let newCodeValue = codeValue;
        if (newCodeValue != null && this.definition.uppercase) {
            if (typeof codeValue === "number") {
                console.warn("The field " + this.definition.property + " is marked to be uppercased but the code is numeric!")
            } else {
                newCodeValue = (codeValue as string)?.toUpperCase();
            }
        }
        return newCodeValue;
    }

    private detailUpdateDoneCallback = (silent) => {
        return (response: Response): void => {
            if (this.currentValue == null && silent !== true) {
                message.show({ key: "searcher.no_code_found", level: "warn" });
            }
        }
    }

    public getValue(): SearcherWithSingleCodeValue {
        if (this.definition.uppercase && this.currentValue != null) {
            this.currentValue.code = this.toUpperCaseCode(this.currentValue.code);
        }
        return this.currentValue;
    }

    public value(v: any): void {
        if (v != null) {
            if (v.code === undefined) {
                v = { code: v };
            }
            if (this.definition.numericCode && typeof v.code === "string") {
                v.code = parseInt(v.code, 10);
            }
            if (v.queryParameters != null) {
                this.queryParameters = v.queryParameters;
            }
            if (v?.code === null) {
                this.oldValue = null;
                this.codeInput.value = null;
                if (this.detailInput != null) {
                    this.detailInput.value = null;
                }
            } else {
                if (this.detailInput != null) {
                    let valueDetail = v.detail ?? null;
                    if (valueDetail != null && this.definition.translateDetail) {
                        valueDetail = i18n.t(valueDetail);
                    }
                    this.detailInput.value = valueDetail;
                }
                if (v.code !== this.oldValue || this.detailInput?.value === "") {
                    this.codeInput.value = v.code;
                    if (v.detail == null
                        && (this.definition.hasDetail !== false
                            || (this.definition.outputParameterMappings != null && this.definition.outputParameterMappings.length > 0))) {
                        this.detailUpdate();
                    }
                }
            }
            this.oldValue = v.code;
        } else {
            this.oldValue = null;
            this.codeInput.value = null;
            if (this.detailInput != null) {
                this.detailInput.value = null;
            }
        }
        this.currentValue = (v == null || v.code == null) ? null : { ...v };
        this.mask?.updateValue();
    }

    /**
     * Used by the rendered as part of the detail update.
     * @param data Data returned by the item finder select or null.
     */
    public updateData(data): void {
        let returnedData;
        let bMultipleData = false;
        if (this.definition.finder.url != null) {
            const mapped = itemFinderModal.map(itemFinderModal.EnumSearcherWithType.SINGLE, this.definition.finder, data);
            this.modalWindowRowSelectedCallback(mapped);
        } else {
            if (data == null || data.data == null || data.data[0] == null || data.data.length === 0) {
                returnedData = [];
                for (const iterator of TableDefinitionUtils.getDataColumns(this.definition.finder.table)) {
                    returnedData.push(null);
                }
                for (const iterator of this.definition.finder.table.extraFields ?? []) {
                    returnedData.push(null);
                }
            } else {
                returnedData = data.data[0];
                if (data.data.length > 1) {
                    console.error("Too many results for finder data. Please, check that your selectors has a code filter field and that the query uses it.", data);
                    bMultipleData = true;
                }
            }

            const tableData = TableDefinitionUtils.rowDataToObject(this.definition.finder.table, returnedData);
            const mapped = itemFinderModal.map(itemFinderModal.EnumSearcherWithType.SINGLE, this.definition.finder, tableData);
            if (bMultipleData) {
                mapped.detail = i18n.instance.t("searcher.multiple_data_returned");
            }
            this.modalWindowRowSelectedCallback(mapped);
        }
    }

    public setReadOnly(ro: boolean): void {
        this.codeInput.readOnly = ro;
        this.magnifierButton.disabled = ro;
    }

    public setRequired(required: boolean) {
        let inputLabel = this.view.querySelector("s6-input-label");
        if (inputLabel != null) {
            (inputLabel as LabelElement).required = required;
        }
    }

    public setValidationStatus(valid: boolean, validationMessage?: string): void {
        this.valid = valid;
        this.validationMessage = validationMessage || null;
        if (valid) {
            this.view.querySelector("input").classList.remove("is-pending");
            this.view.querySelector("input").classList.remove("has-error");
            $(this.view).find(".o-form-component__input-group").removeAttr("data-tip");
        } else {
            if (this.visited) {
                this.view.querySelector("input").classList.add("has-error");
                this.view.querySelector("input").classList.remove("is-pending");
            } else {
                this.view.querySelector("input").classList.remove("has-error");
                this.view.querySelector("input").classList.add("is-pending");
            }
            $(this.view).find(".o-form-component__input-group").attr("data-tip", validationMessage);
        }
    }

    public addOptions(options: OptionHolder): void {
        SearcherWithComposedCode.cleanupOptions(options);
        this.queryParameters = this.queryParameters ?? [];
        const mergeResults = mergeOptions(this.queryParameters, options);
        this.queryParameters = mergeResults.queryParameters;
        if (mergeResults.isOptionsChanged && this.getValue() != null) {
            this.detailUpdate(true);
        }
    }

    public setOptions = this.addOptions;
}

export interface SearcherWithSingleCodeValue {
    code: string | number;
    detail: string;
}

export interface SearcherWithSingleCodeDefinition extends SearcherBaseDefinition, SearcherCodeDefinition,
    SearcherDetailDefinition {
}
